import { Box, Stack } from "@mui/material";
import { SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { ExternalLink } from "@src/appV2/lib/Links/ExternalLink";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { ModalStatus, type UseModalState } from "@src/lib/ui-react";
import type { ReactNode } from "react";

export const AGREE_AND_ACCEPT_LABEL = "Agree and Accept";

export interface WorkplaceAgreementDialogShellProps {
  isOpen: boolean;
  title: string;
  isSubmitting: boolean;
  /** Blocks acceptance while a required field is incomplete; defaults to always acceptable. */
  isAgreeDisabled?: boolean;
  onAgree: () => void;
  children: ReactNode;
}

/**
 * Shared chrome for the blocking workplace-agreement gates. Accepting is the only way out, so
 * every close affordance (backdrop, Escape, swipe, X button) is disabled.
 *
 * Self-wraps in `BerlinThemeProvider` because these dialogs are mounted at the app-layout root,
 * outside any themed subtree — without it they inherit the legacy theme and render with the wrong
 * radii, typography, and button styling (TEM-3613).
 */
export function WorkplaceAgreementDialogShell(props: WorkplaceAgreementDialogShellProps) {
  const { isOpen, title, isSubmitting, isAgreeDisabled = false, onAgree, children } = props;

  const modalState: UseModalState = {
    modalStatus: isOpen ? ModalStatus.OPEN : ModalStatus.CLOSED,
    setModalStatus: () => undefined,
    modalIsOpen: isOpen,
    modalIsClosed: !isOpen,
    openModal: () => undefined,
    closeModal: () => undefined,
  };

  return (
    <BerlinThemeProvider>
      <ResponsiveDialog
        dismissible={false}
        showCloseButton={false}
        mobileVariant="fullScreen"
        modalState={modalState}
        title={title}
        width={480}
        footerContent={
          <Box
            sx={(theme) => ({
              paddingX: theme.spacing(5),
              paddingY: theme.spacing(3),
            })}
          >
            <Stack spacing={3}>
              <LoadingButton
                fullWidth
                variant="primary"
                size="large"
                disabled={isAgreeDisabled}
                isLoading={isSubmitting}
                onClick={onAgree}
              >
                {AGREE_AND_ACCEPT_LABEL}
              </LoadingButton>
              <Text variant="body2" align="center">
                <ExternalLink to={SUPPORT_WEBFORM_URL} underline="hover">
                  Contact Support
                </ExternalLink>
              </Text>
            </Stack>
          </Box>
        }
      >
        {children}
      </ResponsiveDialog>
    </BerlinThemeProvider>
  );
}
